import { Inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
} from '@angular/router';

import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { Config, MW_APP_CONFIG } from '../../../config';
import { MwAuthService } from '../auth.service';
import { MwCorePreferenceService } from '../preference.service';
import { isCompanyProtectedRoute } from '../route.utils';
import { MwUserService } from '../user.service';

@Injectable()
export class MwCompanyGuard implements CanActivate, CanLoad {
  private hasAccess$ = this.userService.userProfile$.pipe(
    filter((profile) => !!profile),
    map((profile) => {
      const hasAccess =
        profile?.hasAccessToModule === true &&
        profile?.companyHasAccessToModule === true;
      if (
        !hasAccess &&
        isCompanyProtectedRoute(
          this.config.environment.skipCompanyProtectionRoutes || []
        )
      ) {
        this.router.navigate(['no-access']);
      }

      return hasAccess;
    }),
    take(1)
  );
  private isActiveCompany$ = this.authService.activeCompany$
    .pipe(
      switchMap((activeCompany) =>
        activeCompany?.companyId && activeCompany.companyId > 0
          ? this.preferenceService.preference$
          : of(false)
      ),
      take(1)
    )
    .pipe(
      map((activeCompany) => {
        if (
          !activeCompany &&
          isCompanyProtectedRoute(
            this.config.environment.skipCompanyProtectionRoutes || []
          )
        ) {
          this.router.navigate(['unauthorized', 'for-company']);
          return false;
        }
        return true;
      })
    );

  constructor(
    private readonly authService: MwAuthService,
    private readonly userService: MwUserService,
    private readonly preferenceService: MwCorePreferenceService,
    private readonly router: Router,
    @Inject(MW_APP_CONFIG) private config: Config
  ) {}
  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> {
    return this.isActiveCompany$.pipe(
      filter((result) => result),
      switchMap(() => this.hasAccess$)
    );
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.isActiveCompany$.pipe(
      filter((result) => result),
      switchMap(() => this.hasAccess$)
    );
  }
}
